import { apiClient } from '../services/api-client';

/**
 * Fetches a FHIR endpoint through the `/fhir` proxy.
 *
 * The response is unvalidated JSON, so `TResource` is an unchecked claim confined to
 * this boundary rather than validation — see the note in `LyticsFetcher`. A search
 * query (`/Task/?patient=...`) resolves to a `Bundle` while a read (`/Patient/{id}`)
 * resolves to the resource itself, so the resource type is caller-supplied; it
 * defaults to `fhir.Bundle` because most call sites here are searches.
 *
 * Returns `undefined` without calling the API when `endPoint` is empty —
 * call sites rely on this to defer a fetch until a key is ready.
 */
const FHIRFetcher = async <TResource = fhir4.Bundle>(
  endPoint: string,
  token: string
): Promise<TResource | undefined> => {
  if (!endPoint || !endPoint.length) {
    return;
  }

  const dataEndPoint = '/fhir' + endPoint;

  const data = await apiClient.get<TResource>(import.meta.env.VITE_API_NAME, dataEndPoint, {
    headers: {
      Authorization: 'Bearer ' + token,
      'Content-Type': 'application/json',
    },
  });
  return data;
};

/**
 * Fetches several FHIR endpoints in parallel, resolving to one entry per URL.
 *
 * Resolves to `undefined` — not an empty array — when given no URLs, which is
 * the existing behaviour the call sites branch on.
 */
/**
 * A revalidation trigger handed down from the component that owns a `useSWR` call
 * to a descendant that only needs to say "refetch now".
 *
 * SWR 0.5.7's `mutate` is `(data?, shouldRevalidate?) => Promise<Data | undefined>`,
 * parameterised by the owning hook's `Data`. Every consumer in this tree calls it
 * with no arguments and either ignores or awaits-and-discards the result, so the
 * payload type is the one thing they do NOT need to agree on. Restating a concrete
 * `Data` in each child's props is what made those props disagree with the hook.
 *
 * The return is `void | Promise<unknown>` because some owners pass SWR's `mutate`
 * straight through while others pass a wrapper that fans out to several caches and
 * returns nothing.
 */
export type RevalidateTrigger = () => void | Promise<unknown>;

/**
 * The return type must stay DECLARED. Inferred, the two arms give
 * `Promise<T[]> | Promise<void>`, and `.then()` on a union of two `Promise` types has no
 * single call signature to resolve — so the callback parameter degrades to `any`. That
 * was the origin of the `any` on every `dataset.entry` / `item.resource` read below this
 * fetcher.
 */
export function MultiFetcher<TResource = fhir4.Bundle>(
  token: string,
  ...urls: string[]
): Promise<(TResource | undefined)[] | void> {
  if (urls.length) {
    return Promise.all(urls.map((url) => FHIRFetcher<TResource>(url, token)));
  } else {
    return Promise.resolve();
  }
}
export default FHIRFetcher;
